import React, { useEffect, useRef, useState } from 'react';
import {
  LuBatteryCharging, LuCircleCheck, LuCircleHelp, LuCirclePause, LuCircleX, LuLoaderCircle, LuRefreshCw, LuTriangleAlert, LuWifiOff,
} from 'react-icons/lu';
import Notice from '../../Common/Notice/Notice';
import Badge from '../../ui/Badge/Badge';
import Button from '../../ui/Button/Button';
import CurrencyAmount from '../../ui/CurrencyAmount/CurrencyAmount';
import { SESSION_STATE as S } from '../../../chargerUx/canonical';
import { copyFor, presentBilling, presentSnapshot } from '../../../chargerUx/copy';
import { formatSessionTime } from '../../../utils/formatDate';
import './CanonicalSessionPanel.less';

// Every state has its own glyph, so the state is never carried by colour alone.
const STATE_ICONS = {
  [S.PREPARING]: LuLoaderCircle,
  [S.CHARGING]: LuBatteryCharging,
  [S.PAUSED]: LuCirclePause,
  [S.RESUMING]: LuRefreshCw,
  [S.COMPLETED]: LuCircleCheck,
  [S.ENDED_EARLY]: LuTriangleAlert,
  [S.FAILED_TO_START]: LuCircleX,
  [S.STATUS_UNKNOWN]: LuWifiOff,
  [S.CHARGER_UNAVAILABLE]: LuCircleHelp,
};

/**
 * CanonicalSessionPanel — the status card for a session whose canonical
 * snapshot the API has provided.
 *
 * Order (the client message pattern): what happened → current status → what
 * happens next → what to do, then energy/cost (final or labelled pending),
 * then freshness and a support reference.
 *
 * The panel never decides a state: everything shown comes from the snapshot.
 * It is updated in place — a new snapshot changes the content, it never
 * navigates. A state change is announced once to assistive technology; the
 * same version arriving again announces nothing.
 *
 * Props:
 *   snapshot     canonical snapshot (required)
 *   stale        nothing heard within the API's freshness window
 *   receivedAt   ms timestamp this device last heard about the session
 *   refreshFailed  the last refresh failed; what is shown is the last confirmed state
 *   onRefresh    manual retry
 *   refreshing   a refresh is in flight
 *   onAction     (actionCode) => void for the one primary next action
 *   showBilling  render the energy/cost block (default true)
 *   compact      history-detail variant: no live affordances
 */
function CanonicalSessionPanel({
  snapshot,
  stale = false,
  receivedAt = null,
  refreshFailed = false,
  onRefresh = null,
  refreshing = false,
  onAction = null,
  showBilling = true,
  compact = false,
  locale,
}) {
  const c = copyFor(locale);
  const view = presentSnapshot(snapshot, locale);
  const billing = showBilling ? presentBilling(snapshot, locale) : null;
  const Icon = STATE_ICONS[view.state] ?? LuCircleHelp;

  // Announce a change of outcome once. Keyed by what the client is told, so a
  // duplicate snapshot (or a new version with the same outcome) is silent.
  const announcedKey = useRef(null);
  const [announcement, setAnnouncement] = useState('');
  const key = `${view.state}|${snapshot?.reasonCode ?? ''}|${view.notice ? 'notice' : ''}`;
  useEffect(() => {
    if (announcedKey.current === key) return;
    const first = announcedKey.current === null;
    announcedKey.current = key;
    // The first render is read as page content; only later changes are announced.
    if (!first) setAnnouncement(`${view.heading}. ${view.body}`);
  }, [key, view.heading, view.body]);

  if (!snapshot) return null;

  const uncertain = view.state === S.STATUS_UNKNOWN || stale || refreshFailed;
  const lastConfirmed = snapshot.lastConfirmed?.sessionState
    ? c.freshness.lastConfirmed(c.state[snapshot.lastConfirmed.sessionState] ?? c.state[S.STATUS_UNKNOWN], formatSessionTime(snapshot.lastConfirmed.at))
    : null;
  const updated = receivedAt ? c.freshness.lastUpdated(formatSessionTime(new Date(receivedAt).toISOString())) : null;
  // An action that needs a confirmed live connection is not offered while the
  // charger's state is uncertain; a terminal outcome keeps its next step.
  const actionAllowed = !!view.actionLabel && !!onAction && (snapshot.terminal || !uncertain);

  return (
    <section
      className={`gc-canonical-panel gc-canonical-panel--${view.state.toLowerCase()} ${compact ? 'is-compact' : ''}`.trim()}
      data-testid="canonical-session-panel"
      data-state={view.state}
      data-version={snapshot.version}
      aria-label={view.heading}
    >
      <Notice
        variant={view.notice}
        icon={<Icon aria-hidden="true" />}
        role={view.urgent ? 'alert' : 'status'}
        title={(
          <span className="gc-canonical-panel__title">
            <span data-testid="canonical-heading">{view.heading}</span>
            <Badge tone={view.tone} className="gc-canonical-panel__badge">{view.label}</Badge>
          </span>
        )}
        actions={actionAllowed ? (
          <Button variant="primary" onClick={() => onAction(view.actionCode)} data-testid="canonical-action">
            {view.actionLabel}
          </Button>
        ) : null}
      >
        <p className="gc-canonical-panel__body" data-testid="canonical-body">{view.body}</p>
        {snapshot.stopRequested && !snapshot.terminal && (
          <p className="gc-canonical-panel__line" data-testid="canonical-stop-requested">{c.stopRequested}</p>
        )}
        {view.notice && (
          <p className="gc-canonical-panel__line gc-canonical-panel__notice" data-testid="canonical-notice">{view.notice}</p>
        )}
        {snapshot.visibility?.reconciling && view.state === S.STATUS_UNKNOWN && (
          <p className="gc-canonical-panel__line" data-testid="canonical-reconciling">{c.freshness.reconciling}</p>
        )}
      </Notice>

      {billing && (
        <dl className="gc-canonical-panel__billing" data-testid="canonical-billing" data-billing-status={billing.status}>
          {billing.status === 'NOT_APPLICABLE' ? (
            <div className="gc-canonical-panel__billing-row">
              <dt className="gc-visually-hidden">{c.billing.cost}</dt>
              <dd data-testid="canonical-billing-label">{billing.label}</dd>
            </div>
          ) : (
            <>
              {billing.energyKwh != null && (
                <div className="gc-canonical-panel__billing-row">
                  <dt>{billing.energyLabel}</dt>
                  <dd data-testid="canonical-energy">
                    {`${Number(billing.energyKwh).toFixed(2)} kWh`}
                    {!billing.final && <span className="gc-canonical-panel__estimate"> · {billing.estimateLabel}</span>}
                  </dd>
                </div>
              )}
              {billing.totalCost != null && (
                <div className="gc-canonical-panel__billing-row">
                  <dt>{billing.costLabel}</dt>
                  <dd data-testid="canonical-cost">
                    <CurrencyAmount value={billing.totalCost} currency={billing.currency || 'BBD'} />
                    {!billing.final && <span className="gc-canonical-panel__estimate"> · {billing.estimateLabel}</span>}
                  </dd>
                </div>
              )}
              {!billing.final && (
                <p className="gc-canonical-panel__pending" data-testid="canonical-billing-label">{billing.label}</p>
              )}
            </>
          )}
        </dl>
      )}

      {(uncertain || lastConfirmed) && !compact && (
        <div className="gc-canonical-panel__freshness" data-testid="canonical-freshness">
          {(stale || refreshFailed) && (
            <p className="gc-canonical-panel__stale" data-testid="canonical-stale">
              <LuTriangleAlert aria-hidden="true" />{' '}
              {refreshFailed ? c.freshness.refreshFailed : c.freshness.stale}
            </p>
          )}
          {lastConfirmed && view.state === S.STATUS_UNKNOWN && <p data-testid="canonical-last-confirmed">{lastConfirmed}</p>}
          {updated && (stale || refreshFailed) && <p data-testid="canonical-last-updated">{updated}</p>}
          {onRefresh && (
            <Button variant="secondary" size="sm" onClick={onRefresh} disabled={refreshing} data-testid="canonical-refresh">
              {refreshing ? c.freshness.refreshing : c.freshness.refresh}
            </Button>
          )}
        </div>
      )}

      {view.supportCode && (view.urgent || view.state === S.STATUS_UNKNOWN) && (
        <p className="gc-canonical-panel__support" data-testid="canonical-support">{c.support(view.supportCode)}</p>
      )}

      {/* One polite live region: state changes are spoken once, not on every render. */}
      <p className="gc-visually-hidden" aria-live="polite" aria-atomic="true" data-testid="canonical-announcer">{announcement}</p>
    </section>
  );
}

export default CanonicalSessionPanel;
